import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-pgt-sr"),
  kind: "test",
  tags: ["testing", "both-partners", "genetic-screening", "ivf", "genomic"],
  title: "PGT-SR (Structural Rearrangement)",
  subtitle: "Tests embryos for chromosomal structural rearrangements",
  icon: "🧬",
  body: {
    testName:
      "Preimplantation Genetic Testing for Structural Rearrangements (PGT-SR)",
    category: "genetic",
    parentTest: {
      testId: makeId("test-preimplantation-genetic-testing"),
      badgeLabel: "View all PGT tests",
    },
    evidenceStrength: "moderate",
    whatItMeasures:
      "Tests embryos for unbalanced chromosome material related to a known structural rearrangement in a genetic parent, such as a balanced translocation, inversion, insertion, or deletion.\n\nThe goal is to identify embryos that are chromosomally normal or balanced for the rearrangement and avoid transferring embryos with missing or extra chromosome segments related to that rearrangement.",
    howItWorks:
      "PGT-SR is planned around the specific rearrangement found on a parent karyotype or chromosome test. Embryos are biopsied at the blastocyst stage and the genetics lab uses a method capable of detecting the expected unbalanced forms. Depending on the method, the report may or may not distinguish a truly normal embryo from a balanced carrier embryo.",
    howItWorksSteps: [
      "A parent karyotype or chromosome test identifies the structural rearrangement.",
      "The genetics lab confirms that its method can detect the expected unbalanced embryo outcomes.",
      "Embryos are created through IVF and biopsied at the blastocyst stage.",
      "The lab tests embryo biopsy DNA for chromosome gains or losses related to the rearrangement.",
      "The clinic reviews which embryos are normal or balanced, unbalanced, mosaic or segmental, or no-result.",
    ],
    quickFacts: [
      { kind: "sample", value: "Embryo biopsy cells" },
      { kind: "time", value: "No extra patient procedure" },
      { kind: "pain", value: "None for patient" },
      { kind: "anesthesia", value: "None for the biopsy" },
      { kind: "prep", value: "Requires parental chromosome result" },
      { kind: "results", value: "1-2 weeks after biopsy" },
    ],
    resultTypes: [
      {
        label: "Normal or balanced",
        meaning:
          "The embryo does not appear to have an unbalanced form of the known rearrangement.",
        whatToExpect:
          "Clinics usually prioritize these embryos, though some reports may not distinguish a truly normal embryo from a balanced-carrier embryo.",
      },
      {
        label: "Unbalanced",
        meaning:
          "The embryo appears to have missing or extra chromosome material related to the structural rearrangement.",
        whatToExpect:
          "Most clinics do not recommend transfer because implantation failure, miscarriage, or an affected pregnancy is more likely.",
      },
      {
        label: "Mosaic or segmental",
        meaning:
          "The biopsy suggests a more complex or partial chromosome finding.",
        whatToExpect:
          "This needs genetic counseling because decisions depend on the chromosomes involved, embryo quality, and whether other embryos are available.",
      },
      {
        label: "No-result",
        meaning: "The lab cannot make a reliable call from the biopsy sample.",
        whatToExpect:
          "Your clinic may discuss re-biopsy, transfer without a result, or deprioritizing the embryo depending on embryo quality and alternatives.",
      },
    ],
    expectations: {
      canExpect: [
        "A test designed around a known parental structural chromosome rearrangement",
        "A report that helps avoid embryos with unbalanced chromosome material related to that rearrangement",
        "Genetic counseling about residual risk, normal versus balanced embryos, and prenatal testing options",
      ],
      shouldNotExpect: [
        "A reason to do PGT-SR without a known structural rearrangement",
        "A guarantee of implantation, live birth, or a child without health conditions",
        "Every lab method to distinguish normal embryos from balanced-carrier embryos",
      ],
    },
    considerations: [
      "Ask whether your rearrangement is reciprocal, Robertsonian, inversion, insertion, deletion, duplication, or more complex.",
      "Ask whether the lab can detect all expected unbalanced forms for your specific rearrangement.",
      "Ask whether the result distinguishes normal from balanced carrier embryos or only identifies embryos without unbalanced material.",
      "Ask how PGT-SR will be combined with PGT-A, embryo grading, and genetic counseling.",
    ],
    bestFor: [
      "Known balanced translocation or other structural rearrangement in a genetic parent",
      "Recurrent pregnancy loss or infertility linked to a parental rearrangement",
      "Couples who want to reduce transfer of embryos with unbalanced chromosome material",
      "Not useful as a generic embryo screening test without a rearrangement",
    ],
    keyFindings: [
      {
        direction: "positive",
        outcome:
          "PGT-SR is specifically intended for embryos at risk of unbalanced structural chromosome rearrangements",
        mechanism:
          "A parent may be healthy with balanced chromosomes, while embryos can inherit missing or extra chromosome material.",
        source: "ACOG Committee Opinion on PGT, 2020",
      },
      {
        direction: "neutral",
        outcome:
          "ESHRE says PGT-SR should use a technique able to detect the expected unbalanced forms",
        mechanism:
          "Different technologies have different inclusion criteria, resolution, and validation needs.",
        source: "ESHRE PGT-SR/PGT-A good practice recommendations, 2020",
      },
      {
        direction: "neutral",
        outcome:
          "Some reports cannot distinguish a normal embryo from a balanced carrier embryo",
        mechanism:
          "That distinction depends on the rearrangement and testing platform, so counseling matters before embryo prioritization.",
        source: "ESHRE PGT-SR/PGT-A good practice recommendations, 2020",
      },
    ],
    turnaroundTime: "1-2 weeks after biopsy",
    costRange: { low: 3000, high: 6000 },
    atHome: false,
    clinicRequired: false,
    dataPoints: [],
    section: "genetic-screening",
  },
  sources: [
    {
      title: "Preimplantation Genetic Testing (Committee Opinion No. 799)",
      authors: "American College of Obstetricians and Gynecologists",
      journal: "Obstetrics & Gynecology",
      year: 2020,
      url: "https://pubmed.ncbi.nlm.nih.gov/32080053/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "ESHRE PGT Consortium good practice recommendations for the detection of structural and numerical chromosomal aberrations",
      authors: "Coonen E, Rubio C, Christopikou D, et al.",
      journal: "Human Reproduction Open",
      year: 2020,
      url: "https://academic.oup.com/hropen/article/2020/3/hoaa017/5848303",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 5,
};

registerBlock(test);
